import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, ToggleOn, ToggleOff, AccountBalanceWallet } from '@mui/icons-material';
import { usePaymentConfigurations, usePermission } from '../hooks';
import { useSnackbar } from '../contexts/SnackbarContext';
import { countyProfileApi } from '../api/countyProfile';
import { colors, transitions, radius } from '../theme/theme';

export const PaymentConfigPage = () => {
  const { can } = usePermission();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [toggleTarget, setToggleTarget] = useState<any>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const { configurations, isLoading, error, toggleStatus, deleteConfiguration } =
    usePaymentConfigurations({ page: 1, per_page: 100 });

  const handleConfirmToggle = async () => {
    if (!toggleTarget) return;
    setBusyId(toggleTarget.id);
    // toggleTarget.is_active is the current state; after toggle it becomes the opposite.
    const willEnable = toggleTarget.is_active === false;
    try {
      await toggleStatus(toggleTarget.id);
      showSnackbar(willEnable ? 'Payment method enabled' : 'Payment method disabled', 'success');
      setToggleTarget(null);
    } catch (e: any) {
      showSnackbar(e?.response?.data?.message || 'Failed to update status', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await deleteConfiguration(deleteTarget.id);
      showSnackbar('Payment configuration removed', 'success');
      setDeleteTarget(null);
    } catch (e: any) {
      showSnackbar(e?.response?.data?.message || 'Failed to delete configuration', 'error');
    } finally {
      setBusyId(null);
    }
  };

  // Wallet activation — a simple on/off toggle. The backend models it as a
  // PaymentModule so the customer portal surfaces it like any other method.
  const { data: walletEnabled = false, isLoading: walletLoading } = useQuery({
    queryKey: ['walletStatus'],
    queryFn: countyProfileApi.getWalletStatus,
  });
  const toggleWalletMutation = useMutation({
    mutationFn: (enabled: boolean) => countyProfileApi.toggleWallet(enabled),
    onSuccess: (enabled) => {
      queryClient.setQueryData(['walletStatus'], enabled);
      showSnackbar(enabled ? 'Wallet payments activated' : 'Wallet payments deactivated', 'success');
    },
    onError: (e: any) => {
      queryClient.invalidateQueries({ queryKey: ['walletStatus'] });
      showSnackbar(e?.response?.data?.message || 'Failed to update wallet setting', 'error');
    },
  });

  const handleOpenDialog = (config?: any) => {
    setSelectedConfig(config || null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedConfig(null);
  };

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Payment Configurations"
          subtitle="Configure payment gateways and integration settings"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Finance & Budget', path: '/finance/dashboard' },
            { label: 'Payment Config' },
          ]}
          action={
            can('payment_config.manage') ? <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Add Configuration
            </Button> : undefined
          }
        />

        {/* Wallet activation — simple on/off, no gateway credentials needed */}
        <Card sx={{ mb: 3, border: `2px solid ${walletEnabled ? colors.success.main : colors.grey[300]}` }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 250 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AccountBalanceWallet sx={{ color: walletEnabled ? colors.success.main : colors.grey[500] }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Wallet Payments</Typography>
                  <Chip
                    label={walletEnabled ? 'Active' : 'Inactive'}
                    color={walletEnabled ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {walletEnabled
                    ? 'Logged-in customers can load their KaunteePay wallet and pay for bills and services from their balance.'
                    : 'Activate to let logged-in customers top up a wallet and pay for bills and services from their balance.'}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={walletEnabled}
                    onChange={(e) => toggleWalletMutation.mutate(e.target.checked)}
                    disabled={walletLoading || toggleWalletMutation.isPending || !can('payment_config.manage')}
                  />
                }
                label={walletEnabled ? 'Enabled' : 'Disabled'}
                labelPlacement="start"
                sx={{ m: 0 }}
              />
            </Box>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load payment configurations. Please try again.
          </Alert>
        )}

        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: colors.grey[50] }}>
                    <TableCell sx={{ fontWeight: 600 }}>Gateway</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {configurations?.data?.map((config: any) => {
                    // The API returns a PaymentModuleConfiguration row with the
                    // related `payment_module` (name/provider/description/status),
                    // not a flat gateway/type/status object.
                    const module = config.payment_module ?? config.paymentModule ?? {};
                    // Per-county enablement is the config row's own is_active
                    // (rows pre-dating the column default to active).
                    const isActive = config.is_active !== false;
                    const rowBusy = busyId === config.id;
                    return (
                    <TableRow
                      key={config.id}
                      hover
                      sx={{ '&:hover': { bgcolor: colors.primary[50] } }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {module.name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{module.provider || 'N/A'}</TableCell>
                      <TableCell>{module.description || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={isActive ? 'active' : 'inactive'}
                          color={isActive ? 'success' : 'default'}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {can('payment_config.manage') && (
                          <Tooltip title={isActive ? 'Disable' : 'Enable'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={rowBusy}
                                onClick={() => setToggleTarget(config)}
                                sx={{ color: isActive ? colors.success.main : colors.grey[500] }}
                              >
                                {rowBusy ? <CircularProgress size={16} /> : isActive ? <ToggleOn fontSize="small" /> : <ToggleOff fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {can('payment_config.manage') && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(config)}
                              sx={{ color: colors.primary.main }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {can('payment_config.manage') && (
                          <Tooltip title="Remove">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={rowBusy}
                                onClick={() => setDeleteTarget(config)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {(!configurations?.data || configurations.data.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No payment configurations found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {selectedConfig ? 'Edit Payment Configuration' : 'Add Payment Configuration'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Gateway</InputLabel>
                <Select
                  defaultValue={selectedConfig?.gateway || ''}
                  label="Gateway"
                >
                  <MenuItem value="mpesa">M-Pesa</MenuItem>
                  <MenuItem value="bank">Bank Transfer</MenuItem>
                  <MenuItem value="card">Card Payment</MenuItem>
                  <MenuItem value="paypal">PayPal</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  defaultValue={selectedConfig?.type || ''}
                  label="Type"
                >
                  <MenuItem value="live">Live</MenuItem>
                  <MenuItem value="sandbox">Sandbox</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="API Key"
                defaultValue={selectedConfig?.api_key || ''}
                type="password"
              />
              <TextField
                fullWidth
                label="Secret Key"
                defaultValue={selectedConfig?.secret_key || ''}
                type="password"
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                defaultValue={selectedConfig?.description || ''}
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  defaultValue={selectedConfig?.status || 'active'}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained">
              {selectedConfig ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirmation */}
        <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Remove payment configuration?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              This removes{' '}
              <strong>
                {(deleteTarget?.payment_module ?? deleteTarget?.paymentModule)?.name || 'this method'}
              </strong>{' '}
              for your county. Customers will no longer be able to pay with it. You can re-add it later.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={busyId === deleteTarget?.id}
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        {/* Enable / disable confirmation */}
        {(() => {
          const willEnable = toggleTarget?.is_active === false;
          const methodName =
            (toggleTarget?.payment_module ?? toggleTarget?.paymentModule)?.name || 'this method';
          return (
            <Dialog open={!!toggleTarget} onClose={() => setToggleTarget(null)} maxWidth="xs" fullWidth>
              <DialogTitle>{willEnable ? 'Enable' : 'Disable'} payment method?</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary">
                  {willEnable ? (
                    <>
                      Customers will be able to pay with <strong>{methodName}</strong> across this county.
                    </>
                  ) : (
                    <>
                      Customers will no longer be able to pay with <strong>{methodName}</strong>. Its
                      settings are kept, so you can re-enable it any time.
                    </>
                  )}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setToggleTarget(null)}>Cancel</Button>
                <Button
                  variant="contained"
                  color={willEnable ? 'success' : 'warning'}
                  onClick={handleConfirmToggle}
                  disabled={busyId === toggleTarget?.id}
                >
                  {willEnable ? 'Enable' : 'Disable'}
                </Button>
              </DialogActions>
            </Dialog>
          );
        })()}
      </Box>
    </AdminLayout>
  );
};
